import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, Download, ExternalLink, Clock, Repeat, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * AddToCalendar — dropdown affordance that lets the meeting host push the
 * session into Google Calendar (via URL params, no OAuth) or Apple Calendar
 * / any other .ics-speaking client (via a signed .ics file download).
 *
 * Users can also change the scheduled time inline before adding: a small
 * datetime-local input at the top of the popover PATCHes the meeting's
 * `scheduled_for`, then the calendar links use the new value.
 *
 * Requires `meeting.id` (used for backend .ics + PATCH). `meeting.title`,
 * `meeting.scheduled_for`, `meeting.cadence` are read as initial state.
 */
const CADENCE_DEFAULT_MIN = { weekly: 30, monthly: 60, quarterly: 120 };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALERT_OPTIONS = [
  { value: 0, label: "At start time" },
  { value: 5, label: "5 minutes before" },
  { value: 10, label: "10 minutes before" },
  { value: 15, label: "15 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 120, label: "2 hours before" },
  { value: 1440, label: "1 day before" },
];

const REPEAT_OPTIONS = [
  { value: "none", label: "One-time", rrule: null, note: "Single event" },
  { value: "weekly", label: "Weekly", rrule: "RRULE:FREQ=WEEKLY;COUNT=13", note: "13 sessions · one quarter" },
  { value: "fortnightly", label: "Fortnightly", rrule: "RRULE:FREQ=WEEKLY;INTERVAL=2;COUNT=6", note: "6 sessions · one quarter" },
  { value: "monthly", label: "Monthly", rrule: "RRULE:FREQ=MONTHLY;COUNT=3", note: "3 sessions · one quarter" },
  { value: "quarterly", label: "Quarterly", rrule: "RRULE:FREQ=MONTHLY;INTERVAL=3;COUNT=4", note: "4 sessions · one year" },
];


export default function AddToCalendar({ meeting, onUpdated, className = "", compact = false }) {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(() => defaultLocalIso(meeting?.scheduled_for));
  const [saving, setSaving] = useState(false);
  const [repeat, setRepeat] = useState(() => {
    const c = (meeting?.cadence || "").toLowerCase();
    return REPEAT_OPTIONS.some((o) => o.value === c) ? c : "none";
  });
  const [alertMin, setAlertMin] = useState(15);
  const [shiftSeries, setShiftSeries] = useState(false);
  const inSeries = !!meeting?.series_id;
  const rootRef = useRef(null);

  // Pull the user's saved alert lead time the first time the popover opens.
  useEffect(() => {
    if (!open) return;
    api.get("/me").then((r) => {
      const v = r.data?.reminder_lead_min;
      if (ALERT_OPTIONS.some((o) => o.value === v)) setAlertMin(v);
    }).catch(() => {});
  }, [open]);

  const changeAlert = async (v) => {
    setAlertMin(v);
    try {
      await api.patch("/me", { reminder_lead_min: v });
    } catch {
      toast.error("Could not save your alert timing.");
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const durationMin = CADENCE_DEFAULT_MIN[(meeting?.cadence || "").toLowerCase()] || 60;
  const repeatOpt = REPEAT_OPTIONS.find((o) => o.value === repeat) || REPEAT_OPTIONS[0];

  const persistIfChanged = async () => {
    if (!when) return null;
    const newIso = new Date(when).toISOString();
    if (meeting.scheduled_for === newIso) return newIso;
    setSaving(true);
    try {
      const deltaMin = meeting.scheduled_for
        ? Math.round((new Date(newIso) - new Date(meeting.scheduled_for)) / 60000)
        : 0;
      if (inSeries && shiftSeries && deltaMin !== 0) {
        // The series endpoint moves this session AND every later one — a
        // separate per-meeting PATCH here would shift the anchor twice.
        const r = await api.patch(`/meetings/series/${meeting.series_id}`, {
          from_meeting_id: meeting.id,
          shift_minutes: deltaMin,
        });
        toast.success(`Moved ${r.data.updated} session${r.data.updated === 1 ? "" : "s"} in the series.`);
        onUpdated && onUpdated({ ...meeting, scheduled_for: newIso }, { refetch: true });
      } else {
        await api.patch(`/meetings/${meeting.id}`, { scheduled_for: newIso });
        onUpdated && onUpdated({ ...meeting, scheduled_for: newIso });
      }
      return newIso;
    } catch (e) {
      toast.error("Could not update meeting time.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openGoogle = async () => {
    const startIso = (await persistIfChanged()) || meeting.scheduled_for || defaultLocalIso(null);
    const start = new Date(startIso);
    const end = new Date(start.getTime() + durationMin * 60 * 1000);
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: meeting.title || "Boardroom session",
      dates: `${gcalStamp(start)}/${gcalStamp(end)}`,
      details: `Boardroom ${meeting.cadence || "weekly"} session · ${meeting.meeting_type || "solo"}.\n\nOpen: ${window.location.origin}/meetings/${meeting.id}`,
      location: `${window.location.origin}/meetings/${meeting.id}`,
    });
    if (repeatOpt.rrule) params.set("recur", repeatOpt.rrule);
    const url = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(url, "_blank", "noopener");
    toast.success("Opening Google Calendar…");
    setOpen(false);
  };

  const downloadIcs = async () => {
    await persistIfChanged();
    // Route hits the backend which resolves scheduled_for server-side after
    // the PATCH lands. We fetch as blob so we can force download rather than
    // navigate away (some browsers show the .ics as text otherwise).
    try {
      const userId = document.cookie.match(/boardroom_user_id=([^;]+)/)?.[1] || localStorage.getItem("boardroom_user_id");
      const r = await fetch(`${BACKEND}/api/meetings/${meeting.id}/calendar.ics?repeat=${repeat}&alarm=${alertMin}`, {
        credentials: "include",
        headers: userId ? { "X-User-Id": userId } : {},
      });
      if (!r.ok) throw new Error("ics fetch failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boardroom-${meeting.id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded — open it to add the session.");
    } catch {
      toast.error("Could not generate calendar file.");
    }
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md transition-colors ${
          compact ? "px-2.5 py-3 sm:py-1.5 text-xs" : "px-3 py-3 sm:py-2 text-sm"
        }`}
        data-testid={`add-to-calendar-trigger-${meeting.id}`}
      >
        <Calendar size={compact ? 12 : 14} />
        <span className="hidden sm:inline">Add to calendar</span>
        <span className="sm:hidden">Calendar</span>
        <ChevronDown size={compact ? 10 : 12} className="text-[#666]" />
      </button>

      {open && (
        <div
          className="fixed z-50 inset-x-3 bottom-3 max-h-[80vh] overflow-y-auto overscroll-contain sm:absolute sm:z-30 sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72 sm:max-h-[70vh] bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg sm:rounded-md p-4 sm:p-3 shadow-2xl"
          data-testid="add-to-calendar-popover"
        >
          {/* Change time */}
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#c89f45] mb-1.5">
            <Clock size={10} className="inline mr-1" /> Session time
          </label>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-2 py-2.5 sm:py-1.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px] sm:min-h-0"
            data-testid="add-to-calendar-when"
          />
          <p className="text-[10px] text-[#8a8a8a] mt-1 font-mono">
            Duration ≈ {durationMin} min · defaults from {meeting.cadence || "weekly"} cadence
          </p>
          {inSeries && (
            <label className="flex items-center gap-1.5 text-[10px] font-mono text-[#8a8a8a] mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={shiftSeries}
                onChange={(e) => setShiftSeries(e.target.checked)}
                className="accent-[#c89f45]"
                data-testid="add-to-calendar-shift-series"
              />
              Move the rest of the series by the same amount
            </label>
          )}

          {/* Recurrence */}
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#c89f45] mt-3 mb-1.5">
            <Repeat size={10} className="inline mr-1" /> Repeat
          </label>
          <select
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-2 py-2.5 sm:py-1.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px] sm:min-h-0"
            data-testid="add-to-calendar-repeat"
          >
            {REPEAT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-[#8a8a8a] mt-1 font-mono" data-testid="add-to-calendar-repeat-note">
            {repeatOpt.note}
          </p>

          {/* Alert lead time */}
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#c89f45] mt-3 mb-1.5">
            <Bell size={10} className="inline mr-1" /> Alert me
          </label>
          <select
            value={alertMin}
            onChange={(e) => changeAlert(Number(e.target.value))}
            className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-2 py-2.5 sm:py-1.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px] sm:min-h-0"
            data-testid="add-to-calendar-alert"
          >
            {ALERT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-[#8a8a8a] mt-1 font-mono">
            Also sets when Boardroom emails / pushes your reminders
          </p>

          <div className="mt-3 flex flex-col gap-1.5">
            <button
              type="button"
              onClick={openGoogle}
              disabled={saving}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-[#121212] text-sm text-[#eaeaea] disabled:opacity-50 transition-colors"
              data-testid="add-to-calendar-google"
            >
              <span className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-sm bg-white flex items-center justify-center text-[10px] font-mono text-[#0a0a0a]">G</span>
                Google Calendar
              </span>
              <ExternalLink size={12} className="text-[#666]" />
            </button>
            <button
              type="button"
              onClick={downloadIcs}
              disabled={saving}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-[#121212] text-sm text-[#eaeaea] disabled:opacity-50 transition-colors"
              data-testid="add-to-calendar-ics"
            >
              <span className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-sm bg-[#eaeaea] flex items-center justify-center text-[10px] font-mono text-[#0a0a0a]"></span>
                Apple / Outlook / Other
              </span>
              <Download size={12} className="text-[#666]" />
            </button>
          </div>

          <p className="text-[10px] text-[#8a8a8a] mt-3 leading-relaxed">
            Your alert timing applies to the .ics file and to Boardroom's own reminders. Google Calendar uses your own default notification setting.
          </p>
        </div>
      )}
    </div>
  );
}


// Format an ISO string (or default now+1h) into the format `<input
// type="datetime-local">` expects — no seconds, no timezone.
function defaultLocalIso(iso) {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  // Round to next quarter-hour on default.
  if (!iso) {
    const q = 15;
    const rem = d.getMinutes() % q;
    if (rem) d.setMinutes(d.getMinutes() + (q - rem));
    d.setSeconds(0);
    d.setMilliseconds(0);
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function gcalStamp(d) {
  // Google Calendar expects YYYYMMDDTHHMMSSZ (UTC).
  return d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}
